import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isWebAuthnSupported, isPlatformAuthenticatorAvailable } from '../services/webauthn';

function Login() {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithEmail, loginWithPasskey } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Check if passkeys are supported
    if (isWebAuthnSupported()) {
      isPlatformAuthenticatorAvailable().then(setPasskeyAvailable);
    }
  }, []);

  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await loginWithEmail(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setError('');

    if (!email) {
      setError('Please enter your email first');
      return;
    }

    setLoading(true);

    try {
      await loginWithPasskey(email);
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = err.message || 'Passkey login failed';

      // Check if it's a "no passkeys registered" error
      if (errorMessage.toLowerCase().includes('no passkeys') ||
          errorMessage.toLowerCase().includes('not registered')) {
        setError('No passkey registered for this email. Please sign up with a passkey first or use password login.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signin-container">
      <div className="signin-form">
        <h2>Sign in to Cointrust.ai</h2>

        <form onSubmit={handleEmailLogin}>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />

          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
          />

          {error && (
            <div style={{ color: 'red', fontSize: '14px', marginBottom: '10px' }}>
              {error}
              {(error.toLowerCase().includes('no passkey') || error.toLowerCase().includes('not registered')) && (
                <div style={{ marginTop: '8px' }}>
                  <Link to="/signup" style={{ color: '#007bff', textDecoration: 'underline' }}>
                    Register a passkey →
                  </Link>
                </div>
              )}
            </div>
          )}

          <button type="submit" className="continue-btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <Link to="/forgot-password" style={{ color: '#007bff', fontSize: '14px', textDecoration: 'underline' }}>
              Forgot your password?
            </Link>
          </div>
        </form>

        <div className="divider">OR</div>

        {passkeyAvailable && (
          <button
            type="button"
            className="oauth-btn"
            onClick={handlePasskeyLogin}
            disabled={loading}
          >
            Sign in with Passkey
          </button>
        )}

        <div className="login-link">
          Don't have an account? <Link to="/signup">Sign up</Link>
        </div>
      </div>
    </div>
  );
}

export default Login;
